import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, PaperPlaneTilt, Spinner, FolderSimple, Bone, ArrowClockwise } from '@phosphor-icons/react';
import { useAuthStore } from '../../stores/authStore';
import type { Asset, Submission } from '@spine-workspace/shared';

export interface SubmitReviewPayload {
  name: string;
  note: string;
}

interface SubmitReviewDialogProps {
  open: boolean;
  onClose: () => void;
  /** The Drive folder being submitted (or asset ID for direct-asset submissions) */
  folderId: string;
  folderName: string;
  /** When true (default), show as Drive folder. When false, show as direct asset. */
  isDriveAsset?: boolean;
  /** Assets in this folder */
  assets: Asset[];
  /** Existing submission if this is a re-submit */
  existingSubmission: Submission | null;
  onConfirm: (payload: SubmitReviewPayload) => Promise<void>;
}

export default function SubmitReviewDialog({
  open, onClose,
  folderId, folderName,
  isDriveAsset = true,
  assets,
  existingSubmission,
  onConfirm,
}: SubmitReviewDialogProps) {
  const user = useAuthStore(s => s.user);
  const isResubmit = Boolean(existingSubmission);

  const defaultName = existingSubmission?.submissionName
    ?? existingSubmission?.assetName
    ?? assets[0]?.name
    ?? folderName
    ?? '';

  const [name, setName] = useState(defaultName);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setName(
        existingSubmission?.submissionName
          ?? existingSubmission?.assetName
          ?? assets[0]?.name
          ?? folderName
          ?? ''
      );
      setNote('');
      setError('');
    }
  }, [open]);

  async function handleConfirm() {
    if (!name.trim()) { setError('Name is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      await onConfirm({ name: name.trim(), note: note.trim() });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  const shortId = folderId.length > 20 ? `${folderId.slice(0, 10)}…${folderId.slice(-6)}` : folderId;

  return (
    <Dialog.Root open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 z-40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50
                     w-[min(520px,calc(100vw-32px))]
                     bg-bg-elevated border border-border rounded-xl shadow-xl
                     focus:outline-none flex flex-col max-h-[90vh]"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border-subtle shrink-0">
            <div className="flex items-center gap-2.5">
              {isResubmit
                ? <ArrowClockwise size={16} className="text-accent" />
                : <PaperPlaneTilt size={16} className="text-accent" />}
              <div>
                <Dialog.Title className="font-serif text-base font-medium text-text-primary">
                  {isResubmit ? 'Re-submit for Review' : 'Submit for Review'}
                </Dialog.Title>
                {isResubmit && existingSubmission && (
                  <p className="text-[11px] font-sans text-text-tertiary mt-0.5">
                    Version {existingSubmission.resubmissionCount + 2} · resets status to Waiting
                  </p>
                )}
              </div>
            </div>
            <Dialog.Close className="w-7 h-7 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-colors">
              <X size={13} />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1 p-5 space-y-4">

            {/* ID */}
            <Field label={isDriveAsset ? 'Drive Folder ID' : 'Asset'}>
              <div className="flex items-center gap-2 bg-bg-input border border-border rounded px-3 py-2">
                {isDriveAsset
                  ? <FolderSimple size={13} className="text-amber-400 shrink-0" />
                  : <Bone size={13} className="text-accent/70 shrink-0" />}
                <span className="text-xs font-sans text-text-secondary flex-1 truncate" title={folderId}>
                  {isDriveAsset ? shortId : (assets[0]?.name ?? folderName)}
                </span>
                {!isDriveAsset && (
                  <span className="text-[10px] font-mono text-text-tertiary shrink-0">{assets[0]?.spineVersion}</span>
                )}
              </div>
            </Field>

            {/* Name */}
            <Field label="Submission Name" hint="Displayed in the review board">
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. MARKIV — Walk Cycle"
                className="w-full bg-bg-input border border-border rounded px-3 py-2 text-sm font-sans text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent transition-colors"
              />
            </Field>

            {/* Assets */}
            <Field label="Assets" hint={isDriveAsset ? 'Read only — all assets in this folder' : undefined}>
              <div className="bg-bg-input border border-border rounded divide-y divide-border-subtle overflow-hidden">
                {assets.length === 0 ? (
                  <p className="px-3 py-2 text-xs font-sans text-text-tertiary">No assets</p>
                ) : assets.map(a => (
                  <div key={a.id} className="flex items-center gap-2 px-3 py-2">
                    <Bone size={12} className="text-accent/60 shrink-0" />
                    <span className="text-xs font-sans text-text-primary flex-1 truncate">{a.name}</span>
                    <span className="text-[10px] font-mono text-text-tertiary shrink-0">{a.spineVersion}</span>
                  </div>
                ))}
              </div>
            </Field>

            {/* Author */}
            <Field label="Submitted by">
              <div className="flex items-center gap-2.5 bg-bg-input border border-border rounded px-3 py-2">
                <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center shrink-0 text-[10px] font-mono text-accent uppercase">
                  {(user?.name ?? user?.email ?? '?')[0]}
                </div>
                <span className="text-sm font-sans text-text-secondary">{user?.name ?? user?.email ?? 'Unknown'}</span>
              </div>
            </Field>

            {/* Previous reviewer note (if resubmit) */}
            {isResubmit && existingSubmission?.reviewNotes && (
              <Field label="Previous reviewer note">
                <div className="bg-bg-input border border-border rounded px-3 py-2 text-xs font-sans text-text-secondary italic">
                  "{existingSubmission.reviewNotes}"
                </div>
              </Field>
            )}

            {/* Submitter note */}
            <Field label="Note for reviewer" hint="Optional — describe what changed or what to look for">
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="e.g. Updated walk cycle timing, please check frame 24–36…"
                rows={3}
                className="w-full bg-bg-input border border-border rounded px-3 py-2 text-sm font-sans text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent transition-colors resize-none"
              />
            </Field>

            {error && (
              <p className="text-xs text-red-400 font-sans">{error}</p>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-3.5 border-t border-border-subtle shrink-0 flex items-center justify-end gap-2.5">
            <button onClick={onClose} disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-sans text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={submitting || !name.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-sans bg-accent text-white hover:bg-accent/80 disabled:opacity-50 transition-colors">
              {submitting ? <Spinner size={13} className="animate-spin" /> : (isResubmit ? <ArrowClockwise size={13} /> : <PaperPlaneTilt size={13} />)}
              {isResubmit ? 'Re-submit' : 'Submit for Review'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline gap-2">
        <label className="text-xs font-mono font-semibold text-text-secondary uppercase tracking-wide">{label}</label>
        {hint && <span className="text-[10px] font-sans text-text-tertiary">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
